import "../../assets/css/ScoreRules.css";
import { useEffect, useState, useCallback, useMemo } from "react";
import { Button, Row, Col, FormGroup, Tooltip } from "reactstrap";
import { ReactComponent as PlusIcon } from "assets/icons/plus_icon.svg";
import { ReactComponent as RemoveIcon } from "assets/icons/remove_icon.svg";
import { ReactComponent as ConditionalIcon } from "assets/icons/conditional_icon.svg";
import { useFieldArray, useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";

const newEmptyField = (
  type,
  groupId,
  conditional = false,
  score = 0,
  isBlockConfiguration = true,
) => {
  if (!conditional)
    return {
      id: generateRandomId(),
      groupId: groupId ?? generateRandomId(),
      type,
      key: "",
      condition: "",
      value: "",
      score: 0,
      priority: false,
    };

  return {
    id: generateRandomId(),
    groupId: groupId,
    score: 0,
    type,
    conditional: "",
  };
};

function generateRandomId() {
  return uuidv4();
}

const ScoreBlockRule = ({
  promptOrEventId,
  type,
  index,
  field,
  keys,
  register,
  append,
  insert,
  errors,
  length,
  lastRule,
  handleRemoveScoreRule,
  watch,
  setValue,
  blockId,
}) => {
  const currentCondition = watch(`${promptOrEventId}.${index}.condition`);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const toggleTooltip = () => setTooltipOpen(!tooltipOpen);

  useEffect(() => {
    if (
      type === "library" &&
      ["CONTAINS", "NOT_CONTAINS"].includes(currentCondition)
    ) {
      setValue(`${promptOrEventId}.${index}.value`, "0");
    }
  }, [currentCondition, type, promptOrEventId, index, setValue]);

  return (
    <>
      <Row key={`row-main-${field.id}`} className="pl-lg-12 mb-2">
        {type !== "library" && "key" in field && (
          <Col xl="3" className="pr-0">
            <label
              htmlFor={`${promptOrEventId}.${index}.key`}
              style={{
                fontSize: "0.9em",
              }}
            >
              Chave
            </label>

            <select
              className="form-control"
              style={{
                borderColor:
                  errors[promptOrEventId] &&
                  errors[promptOrEventId][index] &&
                  errors[promptOrEventId][index].key
                    ? "red"
                    : "#cad1d7",
              }}
              {...register(`${promptOrEventId}.${index}.key`, {
                required: "Necessário informar a chave.",
              })}
            >
              <option></option>
              {type === "prompt" &&
                keys.map((key) => (
                  <option value={key} key={`${field.id}.${key}`}>
                    {key}
                  </option>
                ))}
              {type === "evaluable-event" && (
                <>
                  <option value="response" key={`${field.id}.response`}>
                    response
                  </option>
                  <option value="checklist" key={`${field.id}.checklist`}>
                    checklist
                  </option>
                </>
              )}
            </select>
          </Col>
        )}
        {type === "library" && "key" in field && (
          <Col xl="3" className="pr-0">
            <label
              htmlFor={`${promptOrEventId}.${index}.key`}
              style={{
                fontSize: "0.9em",
              }}
            >
              Cliente / Consultor / Ambos {""}
              <i
                className="fas fa-info-circle"
                id={`canal-tooltip-${promptOrEventId}-${index}`}
                style={{
                  fontSize: "1em",
                  color: "#6c757d",
                  cursor: "pointer",
                }}
              />
              <Tooltip
                placement="right"
                isOpen={tooltipOpen}
                target={`canal-tooltip-${promptOrEventId}-${index}`}
                toggle={toggleTooltip}
              >
                <div>
                  Quando configuradas regras para cliente e consultor, a
                  pontuação será aplicada no primeiro enquadramento que atender
                  aos critérios definidos.
                </div>
              </Tooltip>
            </label>

            <select
              className="form-control"
              style={{
                borderColor:
                  errors[promptOrEventId] &&
                  errors[promptOrEventId][index] &&
                  errors[promptOrEventId][index].key
                    ? "red"
                    : "#cad1d7",
              }}
              {...register(`${promptOrEventId}.${index}.key`, {
                required: "Necessário informar a chave.",
              })}
            >
              <option></option>
              {type === "library" && (
                <>
                  <option value="consultant" key={`${field.id}.consultant`}>
                    consultor
                  </option>
                  <option value="client" key={`${field.id}.client`}>
                    cliente
                  </option>
                  <option value="both" key={`${field.id}.both`}>
                    ambos
                  </option>
                </>
              )}
            </select>
          </Col>
        )}

        {"condition" in field && (
          <Col xl="3" className="pr-0">
            <label
              htmlFor={`${promptOrEventId}.${index}.condition`}
              style={{
                fontSize: "0.9em",
              }}
            >
              Condição
            </label>
            <select
              className="form-control"
              style={{
                borderColor:
                  errors[promptOrEventId] &&
                  errors[promptOrEventId][index] &&
                  errors[promptOrEventId][index].condition
                    ? "red"
                    : "#cad1d7",
              }}
              {...register(`${promptOrEventId}.${index}.condition`, {
                required: "Necessário informar uma condição.",
              })}
            >
              <option></option>
              {type !== "library" && (
                <>
                  <option value={"GREATER_THAN_OR_EQUAL"}>
                    Maior ou igual a
                  </option>
                  <option value={"LESS_THAN_OR_EQUAL"}>Menor ou igual a</option>
                  <option value={"GREATER_THAN"}>Maior que</option>
                  <option value={"LESS_THAN"}>Menor que</option>
                  <option value={"EQUAL_TO"}>Igual a</option>
                  <option value={"NOT_EQUAL_TO"}>Diferente de</option>
                </>
              )}
              {type === "library" && (
                <>
                  <option value={"CONTAINS"}>Contém</option>
                  <option value={"NOT_CONTAINS"}>Não contém</option>
                  <option value={"GREATER_THAN"}>Encontrou mais que</option>
                  <option value={"LESS_THAN"}>Encontrou menos que</option>
                  <option value={"EQUAL_TO"}>Encontrou exatamente</option>
                </>
              )}
            </select>
          </Col>
        )}
        {type === "library" && "value" in field && currentCondition && (
          <>
            {["GREATER_THAN", "LESS_THAN", "EQUAL_TO"].includes(
              currentCondition,
            ) && (
              <Col xl="2" className="pr-0">
                <label
                  htmlFor={`${promptOrEventId}.${index}.value`}
                  style={{
                    fontSize: "0.9em",
                  }}
                >
                  Valor
                </label>
                <input
                  type="number"
                  className="form-control"
                  style={{
                    borderColor:
                      errors[promptOrEventId] &&
                      errors[promptOrEventId][index] &&
                      errors[promptOrEventId][index].value
                        ? "red"
                        : "#cad1d7",
                  }}
                  {...register(`${promptOrEventId}.${index}.value`, {
                    required: "Necessário informar o valor.",
                  })}
                />
              </Col>
            )}
            {["CONTAINS", "NOT_CONTAINS"].includes(currentCondition) && (
              <input
                type="hidden"
                {...register(`${promptOrEventId}.${index}.value`)}
              />
            )}
          </>
        )}
        {type !== "library" && "value" in field && (
          <Col xl="2" className="pr-0">
            <label
              htmlFor={`${promptOrEventId}.${index}.value`}
              style={{
                fontSize: "0.9em",
              }}
            >
              Valor
            </label>
            <input
              type="text"
              className="form-control"
              style={{
                borderColor:
                  errors[promptOrEventId] &&
                  errors[promptOrEventId][index] &&
                  errors[promptOrEventId][index].value
                    ? "red"
                    : "#cad1d7",
              }}
              {...register(`${promptOrEventId}.${index}.value`, {
                required: "Necessário informar o valor.",
              })}
            />
          </Col>
        )}
      </Row>
      <Row key={`row-actions-${field.id}`} className="pl-lg-12 mb-2">
        {"conditional" in field && (
          <Col xl="3" className="pr-0">
            <label
              htmlFor={`${promptOrEventId}.${index}.conditional`}
              style={{
                fontSize: "0.9em",
              }}
            >
              Condicional
            </label>
            <select
              className="form-control"
              style={{
                borderColor:
                  errors[promptOrEventId] &&
                  errors[promptOrEventId][index] &&
                  errors[promptOrEventId][index].conditional
                    ? "red"
                    : "#cad1d7",
              }}
              {...register(`${promptOrEventId}.${index}.conditional`, {
                required: "Necessário informar uma condição.",
              })}
            >
              <option></option>
              <option value={"AND"}>E</option>
              <option value={"OR"}>OU</option>
            </select>
          </Col>
        )}
        <Col
          sm="1"
          className="align-items-center d-flex justify-content-start "
        >
          {lastRule && !("conditional" in field) && (
            <Button
              color="danger"
              size="sm"
              className="mt-4"
              title={
                "conditional" in field ? "Remover condicional" : "Remover regra"
              }
              onClick={() => handleRemoveScoreRule(field.groupId)}
            >
              <RemoveIcon style={{ width: "14px", height: "14px" }} />
            </Button>
          )}
          {!("conditional" in field) && lastRule && (
            <Button
              color="primary"
              size="sm"
              className="mt-4"
              title="Adicionar condicional"
              onClick={() => {
                const groupId = "groupId" in field ? field.groupId : field.id;
                if (length > index) {
                  insert(index + 1, [
                    newEmptyField(type, groupId, true),
                    newEmptyField(type, groupId, false, 0),
                  ]);
                } else {
                  append([
                    newEmptyField(type, groupId, true),
                    newEmptyField(type, groupId, false, 0),
                  ]);
                }
              }}
            >
              <ConditionalIcon style={{ width: "16px", height: "16px" }} />
            </Button>
          )}
          {!("conditional" in field) && lastRule && (
            <Button
              color="success"
              size="sm"
              className="mt-4"
              title="Adicionar nova regra"
              onClick={() => {
                if (length > index) {
                  insert(index + 1, newEmptyField(type));
                } else {
                  append(newEmptyField(type));
                }
              }}
            >
              <PlusIcon style={{ width: "12px" }} />
            </Button>
          )}
        </Col>
      </Row>
    </>
  );
};

const ScoreBlockRules = ({
  promptOrEvent,
  rules,
  type,
  setConfiguredRules,
  open,
  blockId,
}) => {
  const {
    register,
    control,
    watch,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: {
      [promptOrEvent.value]: [newEmptyField(type)],
    },
  });
  const { fields, replace, append, insert, remove } = useFieldArray({
    control,
    name: promptOrEvent.value,
  });

  const response =
    type === "prompt"
      ? createJsonString(
          promptOrEvent.content && Array.isArray(promptOrEvent.content)
            ? promptOrEvent.content.map((section) => ({
                key: section.key,
                value: section.value,
              }))
            : [],
        )
      : promptOrEvent.content;

  function createJsonString(pairs) {
    const jsonObject = {};
    pairs.forEach((pair) => {
      jsonObject[pair.key] = pair.value;
    });
    return JSON.stringify(jsonObject, null, 4);
  }

  const getBlockStorageKey = (itemId) => {
    return `block_${blockId}_score_rules.${itemId}`;
  };

  const promptKeys = useMemo(() => {
    if (
      type === "prompt" &&
      promptOrEvent.content &&
      Array.isArray(promptOrEvent.content)
    ) {
      return promptOrEvent.content.map((section) => section.key);
    }
    return [];
  }, [type, promptOrEvent.content]);

  const handleRemoveScoreRule = useCallback(
    (groupId) => {
      const groupIndexes = [];

      fields.forEach((field, index) => {
        if (field.groupId === groupId || field.id === groupId) {
          groupIndexes.push(index);
        }
      });
      if (groupIndexes?.length == fields?.length) {
        append(newEmptyField(type));
      }
      remove(groupIndexes);
    },
    [fields, append, remove, type],
  );

  const debouncedValidation = useCallback(
    (fieldsData) => {
      const scoreRulesString = JSON.stringify(fieldsData);

      if (
        fieldsData.some((field) => field.key && field.condition && field.value)
      ) {
        setConfiguredRules((prev) => ({
          ...prev,
          [blockId]: {
            ...prev[blockId],
            [promptOrEvent.value]: fieldsData,
          },
        }));
      } else {
        setConfiguredRules((prev) => {
          const updatedRules = { ...prev };
          if (updatedRules[blockId]) {
            const blockRules = { ...updatedRules[blockId] };
            delete blockRules[promptOrEvent.value];

            if (Object.keys(blockRules).length === 0) {
              delete updatedRules[blockId];
            } else {
              updatedRules[blockId] = blockRules;
            }
          }
          return updatedRules;
        });
      }

      const storageKey = getBlockStorageKey(promptOrEvent.value);
      const now = new Date().getTime();
      const expiryTime = now + 24 * 60 * 60 * 1000;
      localStorage.setItem(`block_${blockId}.expiration`, expiryTime);
      localStorage.setItem(storageKey, scoreRulesString);
    },
    [blockId, promptOrEvent.value, setConfiguredRules, getBlockStorageKey],
  );

  useEffect(() => {
    const subscription = watch((value) => {
      const fieldsData = value[promptOrEvent.value];

      const timeoutId = setTimeout(() => {
        debouncedValidation(fieldsData);
      }, 800);

      return () => clearTimeout(timeoutId);
    });

    return () => subscription.unsubscribe();
  }, [watch, debouncedValidation]);

  useEffect(() => {
    if (rules?.length > 0) {
      replace(rules);

      setTimeout(() => {
        rules.forEach((rule, index) => {
          if (rule.key)
            setValue(`${promptOrEvent.value}.${index}.key`, rule.key);
          if (rule.condition)
            setValue(
              `${promptOrEvent.value}.${index}.condition`,
              rule.condition,
            );
          if (rule.value !== undefined)
            setValue(`${promptOrEvent.value}.${index}.value`, rule.value);
          if (rule.score !== undefined)
            setValue(`${promptOrEvent.value}.${index}.score`, rule.score);
          if (rule.priority !== undefined)
            setValue(`${promptOrEvent.value}.${index}.priority`, rule.priority);
          if (rule.conditional)
            setValue(
              `${promptOrEvent.value}.${index}.conditional`,
              rule.conditional,
            );
        });
      }, 100);

      return;
    }

    const storageKey = getBlockStorageKey(promptOrEvent.value);
    const scoreRulesString = localStorage.getItem(storageKey);

    if (scoreRulesString) {
      const fieldsData = JSON.parse(scoreRulesString);
      if (
        fieldsData.some((field) => field.key && field.condition && field.value)
      ) {
        setConfiguredRules((prev) => ({
          ...prev,
          [blockId]: {
            ...prev[blockId],
            [promptOrEvent.value]: fieldsData,
          },
        }));
      }
      replace(fieldsData);

      setTimeout(() => {
        fieldsData.forEach((field, index) => {
          if (field.key)
            setValue(`${promptOrEvent.value}.${index}.key`, field.key);
          if (field.condition)
            setValue(
              `${promptOrEvent.value}.${index}.condition`,
              field.condition,
            );
          if (field.value !== undefined)
            setValue(`${promptOrEvent.value}.${index}.value`, field.value);
          if (field.score !== undefined)
            setValue(`${promptOrEvent.value}.${index}.score`, field.score);
          if (field.priority !== undefined)
            setValue(
              `${promptOrEvent.value}.${index}.priority`,
              field.priority,
            );
          if (field.conditional)
            setValue(
              `${promptOrEvent.value}.${index}.conditional`,
              field.conditional,
            );
        });
      }, 100);
    }
  }, [blockId, rules?.length]);

  useEffect(() => {
    const storageKey = getBlockStorageKey(promptOrEvent.value);
    const StoredScoreRules = localStorage.getItem(storageKey);

    if (fields?.length == 0 && !StoredScoreRules) {
      append(newEmptyField(type));
    }
  }, [fields, blockId]);

  return (
    open && (
      <>
        <div style={{ marginTop: "12px" }}>
          <label
            className="form-control-label"
            htmlFor={`prompt-${blockId}-${promptOrEvent.value}`}
          >
            {type === "prompt" && "Visualização de respostas pré-definidas:"}
            {type === "evaluable-event" &&
              "Visualização de eventos pré-definidos:"}
            {type === "library" && "Visualização de termos:"}
          </label>
          <textarea
            className="input-textarea"
            id={`prompt-${blockId}-${promptOrEvent.value}`}
            type="text"
            value={
              response
                ? response
                : promptOrEvent.content ||
                  (type === "library" &&
                  promptOrEvent.terms &&
                  Array.isArray(promptOrEvent.terms)
                    ? promptOrEvent.terms
                        .map((item) => item.term?.toString())
                        .join(", ")
                    : "")
            }
            disabled
          />
        </div>
        <div className="pl-lg-12 mt-2 box">
          <Col className="pl-lg-12">
            <FormGroup>
              <Row className="align-items-center mb-4 box">
                <Col className="d-flex justify-content-start">
                  <label className="form-control-label">
                    Regras de Score do Bloco
                  </label>
                </Col>
              </Row>
              <FormGroup>
                {fields?.map((field, index) => {
                  const addDivisor =
                    !("conditional" in field) &&
                    index > 0 &&
                    !("conditional" in fields[index - 1])
                      ? true
                      : false;

                  const lastRule =
                    (fields[index + 1] &&
                      !("conditional" in fields[index + 1])) ||
                    index + 1 === fields?.length;
                  return (
                    <div key={field.id}>
                      {addDivisor && (
                        <hr style={{ borderTop: "2px solid gray" }} />
                      )}
                      <ScoreBlockRule
                        promptOrEventId={promptOrEvent.value}
                        type={type}
                        index={index}
                        field={field}
                        keys={promptKeys}
                        register={register}
                        append={append}
                        insert={insert}
                        remove={remove}
                        errors={errors}
                        length={fields.length}
                        lastRule={lastRule}
                        handleRemoveScoreRule={handleRemoveScoreRule}
                        watch={watch}
                        setValue={setValue}
                        blockId={blockId}
                      />
                    </div>
                  );
                })}
              </FormGroup>
            </FormGroup>
          </Col>
        </div>
      </>
    )
  );
};

export const ScoreBlockRuleGroup = ({
  promptsOrEvents,
  type,
  configuredRules,
  setConfiguredRules,
  blockId,
}) => {
  const [promptsAccordions, setPromptsAccordions] = useState([]);

  function handlePromptsAccordions(prompt) {
    if (promptsAccordions.includes(prompt)) {
      setPromptsAccordions((promptsAccordions) =>
        promptsAccordions.filter((m) => m !== prompt),
      );
    } else {
      setPromptsAccordions((promptsAccordions) => [
        ...promptsAccordions,
        prompt,
      ]);
    }
  }

  return (
    <div className="score-group">
      {promptsOrEvents &&
        Array.isArray(promptsOrEvents) &&
        promptsOrEvents.map((promptOrEvent) => {
          const itemRules =
            configuredRules[blockId] &&
            configuredRules[blockId][promptOrEvent.value]
              ? configuredRules[blockId][promptOrEvent.value]
              : [];

          return (
            <Col className="pl-lg-12" key={promptOrEvent.value}>
              <div
                className="pl-lg-12"
                style={{
                  border: "1px solid #65779581",
                  borderRadius: "5px",
                  padding: "8px",
                }}
              >
                <Col className="pl-lg-12" style={{ padding: "0" }}>
                  <button
                    type="button"
                    className="btn-accordion"
                    onClick={() => handlePromptsAccordions(promptOrEvent.value)}
                  >
                    <label
                      className="form-control-label"
                      title={promptOrEvent.value}
                    >
                      <b>
                        {type === "prompt" && "Prompt: "}
                        {type === "evaluable-event" && "Evento: "}
                        {type === "library" && "Biblioteca: "}
                      </b>
                      {promptOrEvent.label}
                    </label>
                    {configuredRules[blockId] &&
                      configuredRules[blockId][promptOrEvent.value] &&
                      configuredRules[blockId][promptOrEvent.value].length >
                        0 && (
                        <span
                          title="Possui regras configuradas!"
                          className="icon-warning"
                        >
                          <i className="ni ni-bullet-list-67 text-orange" />
                        </span>
                      )}
                    <span
                      title="Minimizar/Expandir"
                      style={{
                        width: "36px",
                        margin: "0",
                        border: "1px solid #e9ecef",
                        borderRadius: "4px",
                        marginLeft: !(
                          configuredRules[blockId] &&
                          configuredRules[blockId][promptOrEvent.value]
                        )
                          ? "auto"
                          : "",
                      }}
                    >
                      -
                    </span>
                  </button>
                </Col>
                <ScoreBlockRules
                  promptOrEvent={promptOrEvent}
                  rules={itemRules}
                  type={type}
                  setConfiguredRules={setConfiguredRules}
                  open={promptsAccordions.includes(promptOrEvent.value)}
                  blockId={blockId}
                />
              </div>
            </Col>
          );
        })}
    </div>
  );
};

export default ScoreBlockRuleGroup;
